"use client";

import React from "react";
import { ArrowRight } from "lucide-react";

export interface MemorylayerChallengeBlockProps {
    tag?: string;
    title?: string;
    descParagraphs?: string[];
    linkText?: string;
    linkHref?: string;
}

export default function MemorylayerChallengeBlock({
    tag = "Problem Definition",
    title = "Breaking the Stateless Barrier of AI Blueprints",
    descParagraphs = [
        "Standard generative model loops are transactional. They load the request, parse the prompt, query the parameters, and output raw markdown. Once the browser page closes, the session dies. Achieving true assistant autonomy requires a context loop that endures across different channels.",
        "MemoryLayer resolved this bottleneck by wrapping base foundation model pipelines in a custom state engine. We engineered a scalable pgvector search module, allowing custom personas to retain memories from months prior."
    ],
    linkText = "Analyze Sandbox Compiler",
    linkHref = "#customizer"
}: MemorylayerChallengeBlockProps) {
    return (
        <section className="py-28 px-6 md:px-12 lg:px-24 bg-[#0A0A0C] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        {tag && (
                            <span className="text-xs font-mono text-purple-400 uppercase tracking-widest block mb-4">// {tag}</span>
                        )}
                        <h2 className="text-4xl md:text-5xl font-light text-white leading-tight font-sans">
                            {title === "Breaking the Stateless Barrier of AI Blueprints" ? (
                                <>
                                    Breaking the <span className="text-zinc-600">Stateless Barrier</span> of AI Blueprints
                                </>
                            ) : (
                                title
                            )}
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-zinc-400 text-base leading-relaxed font-light font-sans">
                        {descParagraphs.map((para, i) => (
                            <p key={i}>{para}</p>
                        ))}
                        {linkText && linkHref && (
                            <div className="pt-4">
                                <a href={linkHref} className="text-purple-400 hover:text-white transition-colors flex items-center gap-2 text-xs font-mono">
                                    {linkText} <ArrowRight className="w-3.5 h-3.5" />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
